import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import eventService from '../../services/eventService';
import paymentService from '../../services/paymentService';
import seatMapService from '../../services/seatMapService';
import api from '../../services/api';
import { getEcho } from '../../services/echo';
import CustomerSeatMap from '../../components/seatmap/CustomerSeatMap';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { mediaUrl } from '../../utils/helpers';
import { useLocale } from '../../context/LocaleContext';
import { Map, Marker } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

const EventDetail = () => {
  const { id } = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();
  const { t, money, lang, currency } = useLocale();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTickets, setSelectedTickets] = useState({});
  const [selectedValet, setSelectedValet] = useState(0);
    const [topEvents, setTopEvents] = useState([]);
    const [topIndex, setTopIndex] = useState(0);
  const mapRef = useRef(null);
    const [pauseTopCarousel, setPauseTopCarousel] = useState(false);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const isPurchasingRef = useRef(false);
    useEffect(() => { isPurchasingRef.current = isPurchasing; }, [isPurchasing]);

  // Seat map state
  const [seatMap,        setSeatMap]        = useState(null);
  const [loadingSeatMap, setLoadingSeatMap] = useState(false);
  const [selectedSeats,  setSelectedSeats]  = useState([]);   // array of seat objects
  const [gaSelections,   setGaSelections]   = useState({});   // { [sectionId]: qty }
  const [queueCountdown, setQueueCountdown] = useState(0);
  const [isPurchaseMode, setIsPurchaseMode] = useState(!!location.state?.fromQueue);
  const queueCountdownRef  = useRef(null);
  const softHoldTimerRef   = useRef(null);
  const seatMapRef         = useRef(null); // always current, used in unmount cleanup

    useEffect(() => {
        if (!topEvents.length || pauseTopCarousel) return;

        const interval = setInterval(() => {
            setTopIndex(prev =>
                prev < topEvents.length - 1 ? prev + 1 : 0
            );
        }, 5000);

        return () => clearInterval(interval);
    }, [topEvents, pauseTopCarousel]);
  
  const MAPBOX_TOKEN = 'pk.eyJ1IjoidW5pdGVkc3lzdGVtYSIsImEiOiJjbWlsZWpnMXQwc2NlM2RzNW9wMWR4OWRwIn0.CP-OkFaPzS-Og8wtyZKleQ';

  // Parse geo_location to get latitude and longitude
  const getCoordinates = () => {
    if (!event || !event.geo_location) return null;
    
    const coords = event.geo_location.split(',');
    if (coords.length !== 2) return null;
    
    const latitude = parseFloat(coords[0]);
    const longitude = parseFloat(coords[1]);
    
    if (isNaN(latitude) || isNaN(longitude)) return null;
    
    return { latitude, longitude };
  };

  const mapsHref = () => {
    const coords = getCoordinates();
    if (coords) {
      return `https://www.google.com/maps/search/?api=1&query=${coords.latitude},${coords.longitude}`;
    }
    if (event?.location) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`;
    }
    return null;
  };

  useEffect(() => {
    fetchEventDetail();
  }, [id]);

  // Subscribe to real-time inventory updates for this event
  useEffect(() => {
    let channel;
    try {
      const echo = getEcho();
      channel = echo.channel(`event.inventory.${id}`);
      channel.listen('.seat.status.updated', (e) => {
        if (parseInt(e.event_id) !== parseInt(id)) return;
        if (!e.seats?.length) return;
        const statusMap = {};
        e.seats.forEach(s => { statusMap[s.id] = s.status; });

        setSeatMap(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            sections: prev.sections?.map(sec => ({
              ...sec,
              seats: sec.seats?.map(seat =>
                statusMap[seat.id] !== undefined ? { ...seat, status: statusMap[seat.id] } : seat
              ),
            })),
          };
        });

        // Remove seats from selectedSeats if they are no longer selectable
        // (soft hold released by expiry, or sold by another user completing payment)
        const unavailableIds = e.seats
          .filter(s => s.status === 'available' || s.status === 'sold')
          .map(s => s.id);
        if (unavailableIds.length) {
          setSelectedSeats(prev => {
            const next = prev.filter(s => !unavailableIds.includes(s.id));
            // Toast only when a seat was actually removed from THIS user's
            // selection — not for every viewer receiving the broadcast
            if (next.length < prev.length && !isPurchasingRef.current) {
              toast.warn('Some seats in your selection are no longer available and were removed.');
            }
            return next;
          });
        }
      });

      channel.listen('.inventory.updated', (e) => {
        if (parseInt(e.event_id) !== parseInt(id)) return;

        // Patch ticket category remaining counts
        if (e.ticket_categories?.length) {
          setEvent(prev => {
            if (!prev) return prev;
            const map = {};
            e.ticket_categories.forEach(u => { map[u.id] = u.remaining_tickets; });
            return {
              ...prev,
              ticket_categories: prev.ticket_categories?.map(tc =>
                map[tc.id] !== undefined ? { ...tc, remaining_tickets: map[tc.id] } : tc
              ),
            };
          });
        }

        // Patch GA section remaining capacity in the seat map
        if (e.ga_sections?.length) {
          setSeatMap(prev => {
            if (!prev) return prev;
            const map = {};
            e.ga_sections.forEach(u => { map[u.id] = u.remaining_capacity; });
            return {
              ...prev,
              sections: prev.sections?.map(s =>
                map[s.id] !== undefined ? { ...s, remaining_capacity: map[s.id] } : s
              ),
            };
          });

          // Clamp this user's GA selection if another buyer just locked spots.
          // Skip while THIS user is checking out — the capacity drop is caused by
          // their own hold, and the toast/clamp would wrongly fire for the buyer.
          if (!isPurchasingRef.current) {
            setGaSelections(prev => {
              let changed = false;
              const next = { ...prev };
              e.ga_sections.forEach(u => {
                const qty = next[u.id] || 0;
                if (qty > u.remaining_capacity) {
                  next[u.id] = Math.max(0, u.remaining_capacity);
                  changed = true;
                }
              });
              if (changed) {
                toast.warn('Some General Admission spots were just taken by another buyer — your quantity was adjusted.');
              }
              return changed ? next : prev;
            });
          }
        }
      });
    } catch (_) {}

    return () => {
      try { getEcho().leave(`event.inventory.${id}`); } catch (_) {}
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start countdown timer when returning from WaitingRoom with an expiry time
  useEffect(() => {
    const expiresAt = location.state?.session_expires_at;
    if (!expiresAt) return;

    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
      setQueueCountdown(secs);
      if (secs <= 0) {
        clearInterval(queueCountdownRef.current);
        toast.error('Your purchase session has expired. Please rejoin the queue to buy tickets.');
        navigate('/events', { replace: true });
      }
    };

    tick();
    queueCountdownRef.current = setInterval(tick, 1000);
    return () => clearInterval(queueCountdownRef.current);
  }, [location.state?.session_expires_at]); // eslint-disable-line react-hooks/exhaustive-deps


  // Keep seatMapRef current so unmount cleanup can read it without stale closure
  useEffect(() => { seatMapRef.current = seatMap; }, [seatMap]);

  // Debounced soft-hold sync: every time the seat selection changes, tell the
  // backend which seats this user is holding so other users see them as unavailable.
  useEffect(() => {
    if (!seatMap || !user || !isPurchaseMode) return; // only for seat-map events in purchase mode
    clearTimeout(softHoldTimerRef.current);
    softHoldTimerRef.current = setTimeout(async () => {
      try {
        await seatMapService.syncSoftHold(id, selectedSeats.map(s => s.id));
      } catch (err) {
        const data = err.response?.data;
        if (err.response?.status === 409 && data?.unavailable_seat_ids?.length) {
          setSelectedSeats(prev => prev.filter(s => !data.unavailable_seat_ids.includes(s.id)));
          toast.warn('One or more seats were just taken by another user and removed from your selection.');
        } else {
          console.warn('[soft-hold] sync failed', err.response?.status, err.response?.data);
        }
      }
    }, 400);
    return () => clearTimeout(softHoldTimerRef.current);
  }, [selectedSeats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Release all soft holds when the user leaves the event page
  useEffect(() => {
    return () => {
      if (seatMapRef.current && user) {
        seatMapService.syncSoftHold(id, []).catch(() => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEventDetail = async () => {
    try {
      setLoading(true);
      const response = await eventService.getEvent(id);

      const ev = response.data?.data || response.data;
      setEvent(ev);

      // Try to load a published seat map for this event
      setLoadingSeatMap(true);
      seatMapService.getEventSeatMap(id)
        .then(r => { if (r.data?.seat_map) setSeatMap(r.data.seat_map); })
        .catch(() => {})
        .finally(() => setLoadingSeatMap(false));
    } catch (error) {
      toast.error('Failed to load event details');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  // After a failed hold (quantity already taken by someone else), silently
  // re-fetch availability and clamp every selection to what is really left so
  // the summary card and total no longer show unavailable items.
  const refreshAvailabilityAndClamp = async () => {
    try {
      if (seatMap) {
        const r = await seatMapService.getEventSeatMap(id);
        const fresh = r.data?.seat_map;
        if (fresh) {
          setSeatMap(fresh);
          setGaSelections(prev => {
            const next = {};
            Object.entries(prev).forEach(([secId, qty]) => {
              const sec = fresh.sections?.find(s => String(s.id) === String(secId));
              const remaining = sec?.remaining_capacity ?? sec?.capacity ?? 0;
              next[secId] = Math.min(qty, Math.max(0, remaining));
            });
            return next;
          });
          // Drop selected seats that are no longer held by/available to this user
          setSelectedSeats(prev => prev.filter(sel => {
            const seat = fresh.sections
              ?.flatMap(sec => sec.seats || [])
              .find(s => s.id === sel.id);
            return seat && ['available', 'held'].includes(seat.status);
          }));
        }
      }

      const er = await eventService.getEvent(id);
      const ev = er.data?.data || er.data;
      if (ev?.ticket_categories) {
        setEvent(prev => prev ? { ...prev, ticket_categories: ev.ticket_categories } : prev);
        const valetCat = ev.ticket_categories.find(t => t.type === 'valet');
        if (valetCat) {
          setSelectedValet(v => Math.min(v, categoryAvailable(valetCat)));
        }
        setSelectedTickets(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(tid => {
            const cat = ev.ticket_categories.find(t => t.id === parseInt(tid));
            if (cat) next[tid] = Math.min(next[tid], categoryAvailable(cat));
          });
          return next;
        });
      }
    } catch (_) {}
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'ar' ? 'ar-QA' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      numberingSystem: 'latn',
    });
  };

  // Effective availability for a ticket category — respects the daily limit
  // when the backend provides available_today, bounded by live remaining_tickets
  const categoryAvailable = (cat) => {
    if (!cat) return 0;
    const remaining = cat.remaining_tickets ?? 0;
    const today = cat.available_today;
    return Math.max(0, (today !== undefined && today !== null) ? Math.min(today, remaining) : remaining);
  };

  const formatPrice = (price) => money(price, event?.currency || 'QAR');
  const showRateNote = currency !== (event?.currency || 'QAR');

  const handleTicketQuantityChange = (ticketId, quantity) => {
    setSelectedTickets(prev => {
      if (quantity <= 0) {
        const { [ticketId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [ticketId]: quantity };
    });
  };

  const getTotalAmount = () => {
    if (!event || !event.ticket_categories) return 0;
    
    const ticketsTotal = Object.entries(selectedTickets).reduce((total, [ticketId, quantity]) => {
      const ticket = event.ticket_categories.find(t => t.id === parseInt(ticketId));
      return total + (ticket ? ticket.price * quantity : 0);
    }, 0);
    const valetCategory = event.ticket_categories.find(t => t.type === 'valet');
    const valetTotal = valetCategory ? valetCategory.price * selectedValet : 0;
    return ticketsTotal + valetTotal;
  };

  const getTotalTickets = () => {
    return Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);
  };

    useEffect(() => {
        const fetchTopEvents = async () => {
            try {
                const res = await eventService.getTopEvents();
                setTopEvents(res.data.data || []);
            } catch (e) {
                console.error(e);
            }
        };

        fetchTopEvents();
    }, []);

  // Seat map handlers
  const handleSeatToggle = (seat) => {
    setSelectedSeats(prev => {
      const exists = prev.find(s => s.id === seat.id);
      return exists ? prev.filter(s => s.id !== seat.id) : [...prev, seat];
    });
  };

  const handleGaChange = (sectionId, qty) => {
    setGaSelections(prev => ({ ...prev, [sectionId]: qty }));
  };

  const getSeatMapTotal = () => {
    const seatTotal = selectedSeats.reduce((s, seat) => s + (seat.price || 0), 0);
    const gaTotal   = Object.entries(gaSelections).reduce((s, [secId, qty]) => {
      const section = seatMap?.sections?.find(sec => String(sec.id) === String(secId));
      const price   = section?.price_zone?.price || section?.default_price || 0;
      return s + price * qty;
    }, 0);
    const valetCategory = event?.ticket_categories?.find(t => t.type === 'valet');
    const valetTotal = valetCategory ? valetCategory.price * selectedValet : 0;
    return seatTotal + gaTotal + valetTotal;
  };

  const hasSeatMapSelection = () =>
    selectedSeats.length > 0 || Object.values(gaSelections).some(q => q > 0);

  const getSeatSection = (seat) =>
    seatMap?.sections?.find(sec => (sec.seats || []).some(s => s.id === seat.id));

  // Clicking "Get Tickets" — handles login check, queue check, then enters selection mode
  const handleEnterPurchaseMode = async () => {
    if (!user) {
      toast.info('Please login to purchase tickets');
      navigate('/login', { state: { from: `/events/${id}` } });
      return;
    }

    // Skip queue check when returning from the waiting room
    const fromQueue = location.state?.fromQueue;
    if (!fromQueue) {
      try {
        setIsPurchasing(true);
        const qRes = await api.get(`/events/${id}/queue/status`);
        if (qRes.data?.queue_enabled && qRes.data?.queue_status === 'active') {
          const tokenKey = `queue_token_${id}`;
          const existingToken = localStorage.getItem(tokenKey);
          const body = existingToken ? { session_token: existingToken } : {};

          const joinRes = await api.post(`/events/${id}/queue/join`, body);
          const data = joinRes.data?.data;

          if (data?.session_token) {
            localStorage.setItem(tokenKey, data.session_token);
          }

          if (data?.status === 'admitted' || data?.status === 'purchasing') {
            navigate(`/events/${id}/queue`);
            return;
          } else if (data?.status === 'waiting') {
            toast.info(`You're in the queue at position #${data.position}. Please wait.`);
            navigate(`/events/${id}/queue`);
            return;
          }
        }
      } catch (err) {
        if (err.response?.data?.queue_required) {
          navigate(`/events/${id}/queue`);
          return;
        }
      } finally {
        setIsPurchasing(false);
      }
    }

    setIsPurchaseMode(true);
  };

  // "Proceed to Checkout" — user has already selected; initiate payment
  const handleCheckout = async () => {
    // -- Seat-map checkout -------------------------------------------------
    if (seatMap) {
      if (!hasSeatMapSelection() && selectedValet === 0) {
        toast.warning('Please select at least one seat, General Admission ticket, or valet parking.');
        return;
      }

      try {
        setIsPurchasing(true);
        toast.info('Reserving seats and initiating payment…');

        const gaItems = Object.entries(gaSelections)
          .filter(([, qty]) => qty > 0)
          .map(([section_id, quantity]) => ({ section_id: parseInt(section_id), quantity }));

        const valetCategory = event?.ticket_categories?.find(t => t.type === 'valet');
        const valetItems = selectedValet > 0 && valetCategory
          ? [{ ticket_category_id: valetCategory.id, quantity: selectedValet }]
          : [];

        const response = await seatMapService.holdSeats(id, {
          seat_ids: selectedSeats.map(s => s.id),
          ga_items: gaItems,
          valet_tickets: valetItems,
        });

        if (response.data.success && response.data.pay_url) {
          window.location.href = response.data.pay_url;
        } else {
          toast.error('Failed to initiate payment.');
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to initiate payment.');
        // Quantity/seat no longer available — resync selections with reality
        if (error.response?.status === 422 || error.response?.status === 409) {
          refreshAvailabilityAndClamp();
        }
      } finally {
        setIsPurchasing(false);
      }
      return;
    }

    // -- Standard ticket checkout ------------------------------------------
    const totalTickets = getTotalTickets();
    const valetCategory = event?.ticket_categories?.find(t => t.type === 'valet');
    if (totalTickets === 0 && selectedValet === 0) {
      toast.warning('Please select at least one ticket or valet parking');
      return;
    }

    const tickets = [
      ...Object.entries(selectedTickets).map(([ticketId, quantity]) => ({
        ticket_category_id: parseInt(ticketId),
        quantity,
      })),
      ...(selectedValet > 0 && valetCategory ? [{
        ticket_category_id: valetCategory.id,
        quantity: selectedValet,
      }] : []),
    ];

    try {
      setIsPurchasing(true);
      toast.info('Initiating payment...');

      const response = await paymentService.initiateTicketPayment({
        event_id: parseInt(id),
        tickets,
      });

      if (response.data.success && response.data.pay_url) {
        window.location.href = response.data.pay_url;
      } else {
        toast.error('Failed to initiate payment');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to initiate payment');
      if (error.response?.status === 422 || error.response?.status === 409) {
        refreshAvailabilityAndClamp();
      }
    } finally {
      setIsPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-16 w-16 mx-auto text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Event not found</h2>
          <Link to="/events" className="btn btn-primary">
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  const formatQueueCountdown = (secs) => {
    if (secs <= 0) return '00:00';
    const m = String(Math.floor(secs / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="min-h-screen">
      {/* Queue session countdown banner — portalled into the header so it sticks with the navbar */}
      {location.state?.fromQueue && location.state?.session_expires_at && (() => {
        const slot = document.getElementById('queue-banner-slot');
        if (!slot) return null;
        return ReactDOM.createPortal(
          <div className={`w-full py-2 px-4 text-center text-sm font-semibold text-white ${
            queueCountdown <= 30 ? 'bg-red-500' : queueCountdown <= 60 ? 'bg-orange-500' : 'bg-purple-600'
          }`}>
            {queueCountdown > 0
              ? <>Your purchase session expires in <span className="font-mono text-base">{formatQueueCountdown(queueCountdown)}</span> — complete your order before time runs out</>
              : 'Your purchase session has expired. Please rejoin the queue.'}
          </div>,
          slot
        );
      })()}

      {/* Hero image */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="relative overflow-hidden rounded-3xl bg-gray-900" style={{ height: 380 }}>
          {event?.images && event.images.length > 0 ? (
            <img
              src={mediaUrl(event.images[0].image_path)}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500" />
          )}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <Link to="/events" className="w-9 h-9 rounded-full bg-white text-gray-800 flex items-center justify-center shadow" aria-label={t('backEvents')}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            {event.featured_event && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: '#7C3AED' }}>Top seller</span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Event Details */}
          <div className="lg:col-span-2">
            <h1 className="text-3xl md:text-[2.35rem] font-medium text-gray-900 leading-tight">{event.title}</h1>
            {event.location && (
              <a
                href={mapsHref()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-purple-700 hover:underline"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {event.location}
              </a>
            )}
            {event.description && (
              <div className="mt-5 text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap max-w-3xl">
                {event.description}
              </div>
            )}

            <details className="mt-8 border-t section-fold">
              <summary className="flex items-center justify-between py-4 cursor-pointer font-medium text-gray-900 list-none">
                {t('eventDetails')}
                <svg className="section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </summary>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4 text-sm">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('starts')}</p>
                  <p className="text-gray-900 mt-1">{formatDate(event.event_start)}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('ends')}</p>
                  <p className="text-gray-900 mt-1">{formatDate(event.event_end)}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('organizedBy')}</p>
                  <p className="text-gray-900 mt-1">{event.organizer?.name || 'Event Organizer'}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('category')}</p>
                  <p className="text-gray-900 mt-1">{event.category?.category_name}</p>
                </div>
              </div>
            </details>

            {(loadingSeatMap || seatMap) && (
              <div className="mt-8">
                <h2 className="text-lg font-bold text-gray-900 mb-3">{t('seating')}</h2>
                {loadingSeatMap && <p className="text-sm text-gray-500">{t('loadingMap')}</p>}
                {seatMap && !loadingSeatMap && (
                  <CustomerSeatMap
                    seatMap={seatMap}
                    selectedSeats={selectedSeats}
                    onSeatToggle={handleSeatToggle}
                    gaSelections={gaSelections}
                    onGaChange={handleGaChange}
                    viewOnly={!isPurchaseMode}
                    currency={event?.currency || 'QAR'}
                  />
                )}
                {seatMap && !loadingSeatMap && (() => {
                  const valetCategory = (event?.ticket_categories || []).find(t => t.type === 'valet');
                  if (!valetCategory || valetCategory.remaining_tickets <= 0) return null;
                  return (
                    <div className="mt-5 rounded-xl border-2 border-purple-200 bg-purple-50 px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#8B5CF6,#7C3AED)' }}>
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {valetCategory.category_name}{' '}
                          <span className="text-[10px] font-bold uppercase tracking-wide text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">{t('addOn')}</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">{t('spotsLeft', { count: categoryAvailable(valetCategory) })}</p>
                      </div>
                      <span className="text-sm font-bold text-purple-700 shrink-0">{formatPrice(valetCategory.price)}</span>
                      {isPurchaseMode && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button type="button" aria-label="Remove one valet" onClick={() => setSelectedValet(Math.max(0, selectedValet - 1))} disabled={selectedValet === 0} className="w-7 h-7 rounded-full border-2 border-purple-300 bg-white text-purple-700 font-bold hover:border-purple-500 disabled:opacity-40 flex items-center justify-center">−</button>
                          <span className="w-6 text-center text-sm font-bold text-gray-900">{selectedValet}</span>
                          <button type="button" aria-label="Add one valet" onClick={() => setSelectedValet(Math.min(categoryAvailable(valetCategory), Math.min(valetCategory.ceiling_limit, selectedValet + 1)))} disabled={selectedValet >= Math.min(categoryAvailable(valetCategory), valetCategory.ceiling_limit)} className="w-7 h-7 rounded-full border-2 border-purple-300 bg-white text-purple-700 font-bold hover:border-purple-500 disabled:opacity-40 flex items-center justify-center">+</button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {(() => {
              const coords = getCoordinates();
              
              return (
                <details className="border-t border-b section-fold">
                  <summary className="flex items-center justify-between py-4 cursor-pointer font-medium text-gray-900 list-none">
                    {t('location')}
                    <svg className="section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </summary>
                <div className="pb-4">
                  <div className="flex items-start mb-6">
                    <svg className="w-6 h-6 text-purple-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <a href={mapsHref()} target="_blank" rel="noopener noreferrer" className="text-lg font-medium text-gray-900 hover:text-purple-700 hover:underline">
                        {event.location}
                      </a>
                      {coords && (
                        <p className="text-sm text-gray-500 mt-1">
                          Coordinates: {coords.latitude.toFixed(6)}, {coords.longitude.toFixed(6)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {coords ? (
                    <div className="rounded-lg overflow-hidden" style={{ height: '400px' }}>
                      <Map
                        ref={mapRef}
                        mapboxAccessToken={MAPBOX_TOKEN}
                        initialViewState={{
                          longitude: coords.longitude,
                          latitude: coords.latitude,
                          zoom: 14
                        }}
                        style={{ width: '100%', height: '100%' }}
                        mapStyle="mapbox://styles/mapbox/streets-v12"
                      >
                        <Marker
                          longitude={coords.longitude}
                          latitude={coords.latitude}
                          anchor="bottom"
                        >
                          <div className="flex flex-col items-center">
                            <div className="bg-purple-500 text-white p-2 rounded-lg shadow-lg">
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-purple-500"></div>
                          </div>
                        </Marker>
                      </Map>
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-lg h-96 flex items-center justify-center">
                      <p className="text-gray-500">{t('locationMissing')}</p>
                    </div>
                  )}
                </div>
                </details>
              );
            })()}
              {topEvents.length > 0 && (
                  <section className="w-full px-4 sm:px-6 lg:px-8 mt-10 mb-12">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4 max-w-7xl mx-auto">
                          <h2 className="text-xl font-bold text-gray-900">
                              {t('topEvents')}
                          </h2>

                          {/* Arrows */}
                          <div className="flex gap-2">
                              <button
                                  type="button"
                                  aria-label="Previous top event"
                                  onClick={() => {
                                      setPauseTopCarousel(true);
                                      setTopIndex(i => Math.max(i - 1, 0));
                                  }}
                                  disabled={topIndex === 0}
                                  className="icon-btn"
                              >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                      <path d="M15 19l-7-7 7-7" />
                                  </svg>
                              </button>

                              <button
                                  type="button"
                                  aria-label="Next top event"
                                  onClick={() => {
                                      setPauseTopCarousel(true);
                                      setTopIndex(i => Math.min(i + 1, topEvents.length - 1));
                                  }}
                                  disabled={topIndex >= topEvents.length - 1}
                                  className="icon-btn"
                              >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                      <path d="M9 5l7 7-7 7" />
                                  </svg>
                              </button>
                          </div>
                      </div>

                      {/* Carousel */}
                      {/* IMPORTANT: overflow-x-hidden ONLY */}
                      <div className="overflow-x-hidden rounded-xl shadow-md" onMouseEnter={() => setPauseTopCarousel(true)}
                           onMouseLeave={() => setPauseTopCarousel(false)}>
                          <div
                              className="flex transition-transform duration-500 max-w-7xl mx-auto"
                              style={{
                                  transform: `translateX(-${topIndex * 100}%)`,
                              }}
                          >
                              {topEvents.map(event => (
                                  <Link
                                      key={event.id}
                                      to={`/events/${event.id}`}
                                      className="min-w-full bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-2xl transition flex flex-col md:flex-row overflow-hidden lg:h-[280px] no-underline hover:no-underline"
                                  >
                                      {/* Image */}
                                      <img
                                          src={
                                              event?.images && event.images.length > 0
                                                  ? mediaUrl(event.images[0].image_path)
                                                  : 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?auto=format&fit=crop&w=1200&q=80'
                                          }
                                          alt={event.title}
                                          className="w-full h-64 sm:h-72 md:w-1/2 lg:h-full object-cover"
                                          onError={e => {
                                              e.target.onerror = null;
                                              e.target.src = 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?auto=format&fit=crop&w=1200&q=80';
                                          }}
                                      />

                                      {/* Content */}
                                      <div className="p-4 sm:p-6 flex flex-col justify-between md:w-1/2">
                                          <div>
                                              <h3 className="font-extrabold text-gray-900 text-lg line-clamp-2">
                                                  {event.title}
                                              </h3>

                                              <div className="space-y-1 mt-3">
                                                  {/* Location */}
                                                  <div className="flex items-center text-sm text-gray-900">
                                                      <svg
                                                          className="w-4 h-4 mr-1 text-purple-500"
                                                          fill="none"
                                                          stroke="currentColor"
                                                          viewBox="0 0 24 24"
                                                      >
                                                          <path
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                              strokeWidth="2"
                                                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                                          />
                                                          <path
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                              strokeWidth="2"
                                                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                                          />
                                                      </svg>
                                                      <span>{event.location}</span>
                                                  </div>

                                                  {/* Date */}
                                                  <div className="flex items-center text-sm text-gray-900">
                                                      <svg
                                                          className="w-4 h-4 mr-1 text-purple-500"
                                                          fill="none"
                                                          stroke="currentColor"
                                                          viewBox="0 0 24 24"
                                                      >
                                                          <path
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                              strokeWidth="2"
                                                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                          />
                                                      </svg>
                                                      <span>{formatDate(event.event_start)}</span>
                                                  </div>

                                                  {/* Cheapest price */}
                                                  {event.ticket_categories?.length > 0 && (
                                                      <div className="flex items-center text-sm text-gray-900 mt-1">
                                                          <svg
                                                              xmlns="http://www.w3.org/2000/svg"
                                                              className="w-5 h-5 mr-1"
                                                              viewBox="0 0 24 24"
                                                              fill="#8B5CF6"
                                                          >
                                                              <path d="M9 10a1 1 0 0 0-1 1v2a1 1 0 0 0 2 0v-2a1 1 0 0 0-1-1Zm12 1a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1a1 1 0 0 1 0 2a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1a1 1 0 0 1 0-2Zm-1-1.82a3 3 0 0 0 0 5.64V17H10a1 1 0 0 0-2 0H4v-2.18a3 3 0 0 0 0-5.64V7h4a1 1 0 0 0 2 0h10Z" />
                                                          </svg>

                                                          <span>
                        {t('from')} {money(Math.min(...event.ticket_categories.map(tc => tc.price)), event.currency || 'QAR')}
                      </span>
                                                      </div>
                                                  )}
                                              </div>
                                          </div>

                                          <span className="text-purple-500 font-semibold mt-4">
                {t('view')} →
              </span>
                                      </div>
                                  </Link>
                              ))}
                          </div>
                      </div>
                  </section>
              )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white rounded-2xl hairline p-4 shadow-sm">
              {event.event_start && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {new Date(event.event_start).toLocaleDateString(lang === 'ar' ? 'ar-QA' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'short', numberingSystem: 'latn' })}
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-gray-500">{t('priceFrom')}</p>
                  <p className="text-2xl font-medium tracking-tight text-gray-900">
                    {(() => {
                      const pool = (event.ticket_categories || []).filter(t => t.type !== 'valet');
                      const source = pool.length ? pool : (event.ticket_categories || []);
                      const prices = source.map(t => Number(t.price)).filter(n => Number.isFinite(n));
                      return prices.length ? formatPrice(Math.min(...prices)) : '—';
                    })()}
                  </p>
                  {showRateNote && <p className="text-[11px] text-gray-400 mt-1">{t('approxRate')}</p>}
                </div>
                {!isPurchaseMode && (
                  <button
                    type="button"
                    onClick={handleEnterPurchaseMode}
                    disabled={isPurchasing}
                    className={`shrink-0 px-5 py-3 rounded-xl text-sm font-medium text-white shadow-md shadow-purple-300/60 hover:brightness-110 ${isPurchasing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}
                  >
                    {isPurchasing ? t('pleaseWait') : t('selectTickets')}
                  </button>
                )}
              </div>

              {!seatMap && !loadingSeatMap && (
                <div className="mt-4 border-t soft-line">
                  {(event.ticket_categories || []).filter(t => t.type !== 'valet').map((ticket) => (
                    <div key={ticket.id} className="flex items-center gap-2 py-2.5 border-b soft-line">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{ticket.category_name}</p>
                        <p className="text-xs text-gray-400">{ticket.remaining_tickets > 0 ? t('left', { count: ticket.remaining_tickets }) : t('soldOut')}</p>
                      </div>
                      <span className="text-sm font-medium text-gray-900 shrink-0">{formatPrice(ticket.price)}</span>
                      {isPurchaseMode && ticket.remaining_tickets > 0 && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button type="button" aria-label={`Remove one ${ticket.category_name}`}
                            onClick={() => handleTicketQuantityChange(ticket.id, (selectedTickets[ticket.id] || 0) - 1)}
                            disabled={!selectedTickets[ticket.id]}
                            className="w-7 h-7 rounded-full border border-gray-300 text-gray-700 disabled:opacity-40">−</button>
                          <span className="w-5 text-center text-sm font-semibold">{selectedTickets[ticket.id] || 0}</span>
                          <button type="button" aria-label={`Add one ${ticket.category_name}`}
                            onClick={() => handleTicketQuantityChange(ticket.id, (selectedTickets[ticket.id] || 0) + 1)}
                            disabled={(selectedTickets[ticket.id] || 0) >= Math.min(ticket.remaining_tickets, 10)}
                            className="w-7 h-7 rounded-full border border-gray-300 text-gray-700 disabled:opacity-40">+</button>
                        </div>
                      )}
                    </div>
                  ))}
                  {(() => {
                    const valetCategory = (event.ticket_categories || []).find(t => t.type === 'valet');
                    if (!valetCategory || valetCategory.remaining_tickets <= 0) return null;
                    return (
                      <div className="flex items-center gap-2 py-2.5">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900">{valetCategory.category_name} <span className="text-[10px] font-medium uppercase text-purple-600">{t('addOn')}</span></p>
                          <p className="text-xs text-gray-400">{t('spotsLeft', { count: categoryAvailable(valetCategory) })}</p>
                        </div>
                        <span className="text-sm font-medium text-gray-900 shrink-0">{formatPrice(valetCategory.price)}</span>
                        {isPurchaseMode && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button type="button" aria-label="Remove one valet" onClick={() => setSelectedValet(Math.max(0, selectedValet - 1))} disabled={selectedValet === 0} className="w-7 h-7 rounded-full border border-gray-300 disabled:opacity-40">−</button>
                            <span className="w-5 text-center text-sm font-semibold">{selectedValet}</span>
                            <button type="button" aria-label="Add one valet" onClick={() => setSelectedValet(Math.min(categoryAvailable(valetCategory), Math.min(valetCategory.ceiling_limit, selectedValet + 1)))} disabled={selectedValet >= Math.min(categoryAvailable(valetCategory), valetCategory.ceiling_limit)} className="w-7 h-7 rounded-full border border-gray-300 disabled:opacity-40">+</button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {(seatMap ? (hasSeatMapSelection() || selectedValet > 0) : (getTotalTickets() > 0 || selectedValet > 0)) ? (
                <div className="space-y-4">
                  {/* Seat map selection summary */}
                  {seatMap && selectedSeats.map(s => (
                    <div key={s.id} className="flex justify-between items-center py-2 border-b border-gray-200">
                      <div>
                        <p className="font-medium text-gray-900">Seat {s.label}</p>
                        <p className="text-sm text-gray-500">{s.price_zone?.name || 'Standard'}</p>
                      </div>
                      <p className="font-semibold text-gray-900">{formatPrice(s.price)}</p>
                    </div>
                  ))}
                  {seatMap && Object.entries(gaSelections).filter(([, q]) => q > 0).map(([secId, qty]) => {
                    const section = seatMap.sections?.find(s => String(s.id) === String(secId));
                    const price   = section?.price_zone?.price || section?.default_price || 0;
                    return (
                      <div key={secId} className="flex justify-between items-center py-2 border-b border-gray-200">
                        <div>
                          <p className="font-medium text-gray-900">{section?.name || 'GA'}</p>
                          <p className="text-sm text-gray-500">{qty} × {formatPrice(price)}</p>
                        </div>
                        <p className="font-semibold text-gray-900">{formatPrice(price * qty)}</p>
                      </div>
                    );
                  })}

                  {/* Standard ticket list */}
                  {!seatMap && event.ticket_categories && event.ticket_categories
                    .filter(ticket => selectedTickets[ticket.id] > 0)
                    .map(ticket => (
                      <div key={ticket.id} className="flex justify-between items-center py-2 border-b border-gray-200">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{ticket.category_name}</p>
                          <p className="text-sm text-gray-500">{selectedTickets[ticket.id]} × {formatPrice(ticket.price)}</p>
                        </div>
                        <p className="font-semibold text-gray-900">
                          {formatPrice(ticket.price * selectedTickets[ticket.id])}
                        </p>
                      </div>
                    ))}

                  {/* Valet row */}
                  {selectedValet > 0 && (() => {
                    const valetCat = event?.ticket_categories?.find(t => t.type === 'valet');
                    return valetCat ? (
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <div>
                          <p className="font-medium text-gray-900">{valetCat.category_name}</p>
                          <p className="text-sm text-gray-500">{selectedValet} × {formatPrice(valetCat.price)}</p>
                        </div>
                        <p className="font-semibold text-gray-900">{formatPrice(valetCat.price * selectedValet)}</p>
                      </div>
                    ) : null;
                  })()}

                  {/* Total */}
                  <div className="pt-4 border-t-2 border-gray-300">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-lg font-semibold text-gray-900">{t('total')}</span>
                      <span className="text-2xl font-bold text-purple-500">
                        {formatPrice(seatMap ? getSeatMapTotal() : getTotalAmount())}
                      </span>
                    </div>
                    {showRateNote && <p className="text-[11px] text-gray-400 mb-1">{t('chargedIn', { currency: event?.currency || 'QAR' })}</p>}
                    {!seatMap && (
                      <p className="text-sm text-gray-500">{getTotalTickets() === 1 ? t('ticketOne') : t('ticketCount', { count: getTotalTickets() })}</p>
                    )}
                  </div>

                  {/* Purchase Button */}
                  <button onClick={handleCheckout} disabled={isPurchasing}
                    className={`btn !bg-purple-500 text-white text-semibold w-full mt-6 py-4 text-lg font-semibold ${
                      isPurchasing ? 'opacity-50 cursor-not-allowed' : ''
                    }`}>
                    {isPurchasing ? t('processing') : t('proceed')}
                  </button>
                </div>
              ) : null}

              {/* Event Stats */}
              <div className="mt-8 pt-6 border-t border-gray-200 space-y-3">
                {/*<div className="flex items-center text-sm text-gray-600">*/}
                {/*  <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
                {/*    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />*/}
                {/*  </svg>*/}
                {/*  {event.ticket_categories ? */}
                {/*    event.ticket_categories.reduce((sum, t) => sum + (t.quantity - t.remaining_tickets), 0) : 0*/}
                {/*  } tickets sold*/}
                {/*</div>*/}
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Sale ends {formatDate(event.sale_end)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* -- Floating bottom bar — visible on tickets tab in purchase mode when something is selected -- */}
      {isPurchaseMode && (seatMap ? (hasSeatMapSelection() || selectedValet > 0) : (getTotalTickets() > 0 || selectedValet > 0)) && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50 }}
          className="bg-white border-t border-gray-200 shadow-2xl lg:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-4">
              {/* Selected items */}
              <div className="flex-1 flex items-center gap-3 overflow-x-auto min-w-0">
                {seatMap && selectedSeats.map(s => {
                  const sec = getSeatSection(s);
                  return (
                    <span key={s.id}
                      className="flex-shrink-0 bg-purple-500 text-white text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
                      {sec ? `${sec.name} · ` : ''}{s.label}
                      <button onClick={() => handleSeatToggle(s)}
                        className="ml-1 text-purple-100 hover:text-white leading-none">✕</button>
                    </span>
                  );
                })}
                {seatMap && Object.entries(gaSelections).filter(([, q]) => q > 0).map(([secId, qty]) => {
                  const section = seatMap.sections?.find(s => String(s.id) === String(secId));
                  const remaining = section?.remaining_capacity ?? section?.capacity ?? 0;
                  const price = section?.price_zone?.price || section?.default_price || 0;
                  return (
                    <div key={secId}
                      className="flex-shrink-0 flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-xl px-3 py-1.5">
                      <div className="flex flex-col leading-tight">
                        <span className="text-xs font-semibold text-gray-800">{section?.name || 'GA Zone'}</span>
                        <span className="text-[10px] text-purple-700 font-medium uppercase tracking-wide">{t('gaZone')}{price ? ` · ${formatPrice(price)}` : ''}</span>
                      </div>
                      <div className="flex items-center gap-1.5 ml-1">
                        <button
                          onClick={() => handleGaChange(secId, Math.max(0, qty - 1))}
                          className="w-6 h-6 rounded-full bg-white border border-gray-300 hover:bg-gray-200 text-gray-700 font-bold text-sm flex items-center justify-center leading-none">
                          −
                        </button>
                        <span className="w-5 text-center text-sm font-bold text-gray-900">{qty}</span>
                        <button
                          onClick={() => handleGaChange(secId, Math.min(remaining, qty + 1))}
                          className="w-6 h-6 rounded-full bg-white border border-gray-300 hover:bg-gray-200 text-gray-700 font-bold text-sm flex items-center justify-center leading-none">
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!seatMap && event.ticket_categories && event.ticket_categories
                  .filter(t => t.type !== 'valet' && selectedTickets[t.id] > 0)
                  .map(t => (
                    <span key={t.id}
                      className="flex-shrink-0 bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">
                      {t.category_name} × {selectedTickets[t.id]}
                    </span>
                  ))}
                {selectedValet > 0 && (
                  <span className="flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1" style={{ background: '#EDE9FE', color: '#7C3AED' }}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                    </svg>
                    {t('valet')} × {selectedValet}
                  </span>
                )}
              </div>

              {/* Total + checkout */}
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="text-right">
                  <p className="text-xs text-gray-500">{t('total')}</p>
                  <p className="text-xl font-bold text-purple-500">
                    {formatPrice(seatMap ? getSeatMapTotal() : getTotalAmount())}
                  </p>
                  {showRateNote && <p className="text-[11px] text-gray-400">{t('chargedIn', { currency: event?.currency || 'QAR' })}</p>}
                  {location.state?.fromQueue && queueCountdown > 0 && (
                    <p className={`text-xs font-mono font-semibold ${queueCountdown <= 30 ? 'text-red-500' : 'text-orange-500'}`}>
                      {t('timeLeft', { time: formatQueueCountdown(queueCountdown) })}
                    </p>
                  )}
                </div>
                <button onClick={handleCheckout} disabled={isPurchasing}
                  className={`btn !bg-purple-500 text-white font-semibold px-8 py-3 text-base whitespace-nowrap ${
                    isPurchasing ? 'opacity-50 cursor-not-allowed' : ''
                  }`}>
                  {isPurchasing ? t('processing') : t('proceed')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;

